import { Children, createContext, useCallback } from 'react'
import { useTheme } from 'styled-components'

import { FlexColumn } from '../../components/layout'
import { Pagination } from './Pagination'
import { Slides } from './Slides'

import type { Dispatch, ReactNode, SetStateAction } from 'react'

/**
 * Using AnimatePresence and drag for a slideshow and AnimateSharedLayout
 * for a pagination indicator.
 */

type PaginatedContextType = {
  currentSlide: number
  direction: number
  setCurrentSlideIndex: (newSlide: number, newDirection?: number) => void
  slides?: ReactNode[]
}
const initialContext: PaginatedContextType = {
  currentSlide: 0,
  direction: 1,
  setCurrentSlideIndex: () => null,
}
export const PaginatedViewContext = createContext<PaginatedContextType>(initialContext)

type Props = {
  children: ReactNode
  currentSlideInfo: [number, number]
  setCurrentSlideInfo: Dispatch<SetStateAction<[number, number]>>
}
export function PaginatedView({ children, currentSlideInfo: [currentSlide, direction], setCurrentSlideInfo }: Props) {
  const theme = useTheme()
  /*
   * We keep track of the pagination direction as well as current page, this way we
   * can dynamically generate different animations depending on the direction of travel
   */

  const setCurrentSlideIndex = useCallback(
    (newSlide: number, newDirection?: number) => {
      setCurrentSlideInfo(([previousCurrentSlide]) => {
        if (!newDirection) {
          newDirection = newSlide - previousCurrentSlide
        }

        return [newSlide, newDirection]
      })
    },
    [setCurrentSlideInfo],
  )

  return (
    <PaginatedViewContext.Provider
      value={{
        ...initialContext,
        currentSlide,
        direction,
        setCurrentSlideIndex,
        slides: Children.toArray(children),
      }}
    >
      <FlexColumn flexGrow={1} gap={theme.UI.Spacing.Space4}>
        <Slides />
        <Pagination />
      </FlexColumn>
    </PaginatedViewContext.Provider>
  )
}
